import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button, Input, Select, Switch, Form, Tabs, Tag, message, Spin, List, Empty, InputNumber, DatePicker, Popconfirm, Tooltip } from 'antd';
import { ArrowLeftOutlined, RightOutlined, LoginOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { getUser, updateUser } from '../api/users';
import api from '../api/axios';
import { getBranches } from '../api/branches';
import { getStudents } from '../api/students';
import UserPermissionsPanel from '../components/user/UserPermissionsPanel';
import PersonnelFilesTab from '../components/user/PersonnelFilesTab';
import UserLeavesTab from '../components/user/UserLeavesTab';
import ParentProfileChangeRequestsTab from '../components/user/ParentProfileChangeRequestsTab';
import useAuthStore from '../store/authStore';
import { canManagePermissionsOf } from '../utils/roleLevel';
import PhoneInput from '../components/common/PhoneInput';
import { apiErrorMessage } from '../utils/apiError';

const { Option } = Select;
const { TabPane } = Tabs;

const roleLabels = {
  developer_admin: { label: 'Geliştirici Admin', color: 'purple' },
  super_admin: { label: 'Süper Admin', color: 'red' },
  branch_manager: { label: 'Şube Müdürü', color: 'blue' },
  teacher: { label: 'Öğretmen', color: 'green' },
  stajyer: { label: 'Stajyer', color: 'cyan' },
  parent: { label: 'Veli', color: 'orange' },
};

const ProfileTab = ({ user, branches, onUpdated, viewerIsDeveloperAdmin, viewerCanEditBirthDate }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isDeveloperAdmin = user.role === 'developer_admin';

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      const res = await updateUser(user.id, {
        ...values,
        // DatePicker dayjs nesnesi doner; API tarih bekliyor.
        // Alan temizlendiyse null gonderilmeli ki kayit silinebilsin.
        birth_date: values.birth_date ? values.birth_date.format('YYYY-MM-DD') : null,
      });
      message.success('Kullanıcı güncellendi.');
      onUpdated(res.data);
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      style={{ maxWidth: 500 }}
      initialValues={{
        name: user.name,
        surname: user.surname,
        email: user.email,
        phone: user.phone,
        birth_date: user.birth_date ? dayjs(user.birth_date) : null,
        role: user.role,
        branch_id: user.branch_id,
        daily_wage: user.daily_wage,
        is_active: user.is_active,
      }}
      onFinish={handleSubmit}
    >
      <div className="grid grid-cols-2 gap-4">
        <Form.Item name="name" label="Ad" rules={[{ required: true, message: 'Ad giriniz.' }]}>
          <Input placeholder="Ad" maxLength={100} />
        </Form.Item>
        <Form.Item name="surname" label="Soyad" rules={[{ required: true, message: 'Soyad giriniz.' }]}>
          <Input placeholder="Soyad" maxLength={100} />
        </Form.Item>
      </div>
      <Form.Item
        name="email"
        label="E-posta"
        rules={[
          { required: true, message: 'E-posta giriniz.' },
          { type: 'email', message: 'Geçerli e-posta giriniz.' },
        ]}
      >
        <Input placeholder="ornek@email.com" maxLength={150} />
      </Form.Item>
      <Form.Item
        name="phone"
        label="Telefon"
        rules={[{ pattern: /^\+\d{8,15}$/, message: 'Ülke kodunu seçip numarayı giriniz.' }]}
      >
        <PhoneInput />
      </Form.Item>
      {/* Dogum tarihi YALNIZCA yonetici tarafindan girilir - backend de
          ayni kurali uyguluyor (UserController::birthDateField). Kisi kendi
          profilinden degistiremez, cunku bu alan diger personele bildirim
          uretiyor ve kullanici listesinde herkese gorunuyor. */}
      {viewerCanEditBirthDate && (
        <Form.Item
          name="birth_date"
          label="Doğum Tarihi"
          extra="Doğum gününden bir gün önce şubedeki personele bildirim gider. Yaş hiçbir yerde gösterilmez."
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD.MM.YYYY"
            placeholder="Seçiniz"
            allowClear
            // Gelecek bir tarih dogum tarihi olamaz.
            disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
          />
        </Form.Item>
      )}
      <Form.Item name="role" label="Rol" rules={[{ required: true, message: 'Rol seçiniz.' }]}>
        <Select placeholder="Rol seçiniz" disabled={isDeveloperAdmin}>
          {viewerIsDeveloperAdmin && <Option value="super_admin">Süper Admin</Option>}
          <Option value="branch_manager">Şube Müdürü</Option>
          <Option value="teacher">Öğretmen</Option>
          <Option value="stajyer">Stajyer</Option>
          <Option value="parent">Veli</Option>
        </Select>
      </Form.Item>
      <Form.Item name="branch_id" label="Şube">
        <Select placeholder="Şube seçiniz" allowClear>
          {branches.map((branch) => (
            <Option key={branch.id} value={branch.id}>{branch.name}</Option>
          ))}
        </Select>
      </Form.Item>
      {/* Günlük ücret yalnızca stajyerde anlamlı. Backend ayrıca
          interns.payments.manage izni arıyor — öğretmen bu alanı gönderse
          bile yok sayılıyor (formda gizlemek yetki değildir). */}
      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.role !== cur.role}>
        {({ getFieldValue }) =>
          getFieldValue('role') === 'stajyer' ? (
            <Form.Item
              name="daily_wage"
              label="Günlük Ücret"
              extra="Stajyerin geldiği gün başına alacağı tutar. Onaylanan güne o günkü ücret yazılır; sonradan değiştirmek geçmiş günleri etkilemez."
            >
              <InputNumber min={0} style={{ width: '100%' }} addonAfter="₺" placeholder="Örn. 500" />
            </Form.Item>
          ) : null
        }
      </Form.Item>
      {!isDeveloperAdmin && (
        <Form.Item name="is_active" label="Durum" valuePropName="checked">
          <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
        </Form.Item>
      )}
      <Form.Item className="mb-0">
        <Button type="primary" htmlType="submit" loading={saving}>
          Güncelle
        </Button>
      </Form.Item>
    </Form>
  );
};

// Veli kullanıcı detayına girildiğinde bağlı öğrenciler hiç listelenmiyordu -
// /students?parent_id= filtresi zaten backend'de vardı (StudentController::index),
// sadece web tarafında bu sekme kurulmamıştı.
const ParentChildrenTab = ({ userId }) => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getStudents({ parent_id: userId, per_page: 100 })
      .then((res) => setChildren(res.data.data || []))
      .catch(() => message.error('Öğrenciler yüklenemedi.'))
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <List
      loading={loading}
      locale={{ emptyText: <Empty description="Bağlı öğrenci yok." /> }}
      dataSource={children}
      renderItem={(s) => (
        <List.Item
          className="cursor-pointer hover:bg-gray-50 px-2 rounded"
          onClick={() => navigate(`/students/${s.id}`)}
          actions={[<RightOutlined key="go" className="text-gray-400" />]}
        >
          <List.Item.Meta
            title={`${s.name} ${s.surname}`}
            description={s.branch?.name}
          />
        </List.Item>
      )}
    />
  );
};

const UserDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user: currentUser, startImpersonation } = useAuthStore();
  const [user, setUser] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getUser(id),
      getBranches({ per_page: 100 }),
    ]).then(([userRes, branchesRes]) => {
      setUser(userRes.data);
      setBranches(branchesRes.data.data);
    }).catch(() => {
      message.error('Kullanıcı yüklenemedi.');
      navigate('/users');
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading || !user) {
    return <div className="flex justify-center py-12"><Spin size="large" /></div>;
  }

  const canManagePermissions = canManagePermissionsOf(currentUser, user);
  const viewerIsDeveloperAdmin = currentUser?.role === 'developer_admin';

  // Dogum tarihini yalnizca yonetici girer VE kendi kaydinda giremez -
  // backend'deki kuralin (UserController::birthDateField) aynisi. Alan
  // baskalarina bildirim urettigi icin kisiye kendi kaydini oynatmiyoruz.
  // Velilerde anlamsiz: dogum gunu bildirimi yalnizca personel arasinda.
  const viewerCanEditBirthDate =
    ['super_admin', 'developer_admin', 'branch_manager'].includes(currentUser?.role)
    && currentUser?.id !== user.id
    && user.role !== 'parent';
  const showStaffTabs = canManagePermissions && user.role !== 'parent';

  // Semantik OR (menü/route kapılarıyla aynı kural): rol yeterse ya da izin
  // kişi bazında verilmişse görünür. Asıl kapı backend'de — burada gizlemek
  // yetki kontrolü değildir.
  const canSeeChangeRequests =
    ['super_admin', 'developer_admin', 'branch_manager'].includes(currentUser?.role)
    || (currentUser?.permissions || []).includes('users.profile-change-requests');

  // TAKLİT ET — kural Users.jsx'teki listeyle BİREBİR AYNI.
  //
  // Öğrenci detayından veliye geçilebiliyor ama taklit düğmesi yalnızca
  // kullanıcı listesindeydi; kontrol için listeye dönüp aynı kişiyi
  // yeniden aramak gerekiyordu (kullanıcının isteği, 2026-09-07).
  //
  // `developer_admin` hiç taklit edilemez; `super_admin`i yalnızca
  // developer_admin taklit edebilir. Kendini taklit etme ayrıca
  // engellenmiyor çünkü bu iki kural zaten kapsıyor: taklit yetkisi olan
  // herkesin rolü bu listede.
  const canImpersonate =
    ['super_admin', 'developer_admin'].includes(currentUser?.role)
    && !['developer_admin', ...(viewerIsDeveloperAdmin ? [] : ['super_admin'])].includes(user.role);

  const handleImpersonate = async () => {
    try {
      const res = await api.post(`/admin/impersonate/${user.id}`);
      startImpersonation(res.data.user, res.data.impersonation_id);
      message.success(`${user.name} ${user.surname} olarak giriş yapıldı.`);
      navigate('/dashboard');
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/users')}>
          Geri
        </Button>
        <h2 className="text-2xl font-bold mb-0">
          {user.name} {user.surname}
        </h2>
        <Tag color={roleLabels[user.role]?.color}>{roleLabels[user.role]?.label || user.role}</Tag>
        {canImpersonate && (
          <Popconfirm
            title={`${user.name} ${user.surname} olarak giriş yapılsın mı?`}
            onConfirm={handleImpersonate}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button icon={<LoginOutlined />} size="small">
              Taklit Et
            </Button>
          </Popconfirm>
        )}

        {/* Listedeki ikonun aynısı. Metin sunucuda hazırlanıp WhatsApp'a
            taşınıyor, GÖNDER'e yönetici basıyor.

            `user.whatsapp` alanını backend yalnızca users.whatsapp izni
            olana ve numarası çözülebilen VELİLERE koyuyor — alan yoksa
            düğme de yok, ayrıca rol kontrolüne gerek yok. */}
        {user.whatsapp && (
          <Tooltip title="Veli portalı bilgilendirme mesajı gönder">
            <Button
              icon={<WhatsAppOutlined />}
              size="small"
              href={`https://wa.me/${user.whatsapp.phone}?text=${encodeURIComponent(user.whatsapp.text)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#25D366', borderColor: '#25D366' }}
            >
              WhatsApp
            </Button>
          </Tooltip>
        )}
      </div>

      {/* Bildirimden gelen bağlantı ?tab=requests taşıyor; yönetici tek
          tıkla doğrudan isteği okuyacağı sekmede açılsın. */}
      <Tabs defaultActiveKey={searchParams.get('tab') || 'profile'}>
        <TabPane tab="Profil Bilgileri" key="profile">
          <ProfileTab user={user} branches={branches} onUpdated={setUser} viewerIsDeveloperAdmin={viewerIsDeveloperAdmin} viewerCanEditBirthDate={viewerCanEditBirthDate} />
        </TabPane>
        {canManagePermissions && (
          <TabPane tab="Yetkiler" key="permissions">
            <UserPermissionsPanel user={user} />
          </TabPane>
        )}
        {user.role === 'parent' && (
          <TabPane tab="Bağlı Öğrenciler" key="children">
            <ParentChildrenTab userId={user.id} />
          </TabPane>
        )}
        {/* Bildirimden buraya tıklanarak geliniyor (?tab=requests). Yalnızca
            veli hesabında ve yalnızca izin varsa — istek metni velinin
            kişisel bilgilerini taşıyor. */}
        {user.role === 'parent' && canSeeChangeRequests && (
          <TabPane tab="İstekleri" key="requests">
            <ParentProfileChangeRequestsTab userId={user.id} />
          </TabPane>
        )}
        {showStaffTabs && (
          <TabPane tab="Özlük" key="personnel-files">
            <PersonnelFilesTab userId={user.id} canManage={showStaffTabs} />
          </TabPane>
        )}
        {showStaffTabs && (
          <TabPane tab="İzinler" key="leaves">
            <UserLeavesTab userId={user.id} canManage={showStaffTabs} />
          </TabPane>
        )}
      </Tabs>
    </div>
  );
};

export default UserDetail;
